import React, {
  ComponentType,
  useCallback,
  useEffect,
  useRef,
  useState,
  CSSProperties,
} from 'react';

import { CopyAttachmentsFromProjectFilesModal } from '@adsk/acc-reference-picker';
import { Box } from '@adsk/alloy-react-box';
import { noop } from '@adsk/alloy-react-helpers';
import { EntityItem } from '@adsk/docs-js-sdk';

import { AttachmentsContext } from './AttachmentsContext';
import { UPLOAD_METHODS } from '../consts';
import useAttachmentsManager from '../hooks/useAttachmentsManager';
import useBrowserConfirmationBeforeTabClose from '../hooks/useBrowserConfirmationBeforeTabClose';
import useCallbackOnPasteFiles from '../hooks/useCallbackOnPasteFiles';
import useCapacityAlert from '../hooks/useCapacityAlert';
import AttachmentsViewerProvider from '../layout/AttachmentsViewer/AttachmentsViewerProvider';
import { Attachment, AttachmentDraft } from '../types';
import { LocalViewsProvider } from './LocalViewsProvider';
import { useTranslation } from '../i18n';
import { AttachmentsTooManyFilesModal } from '../layout/AttachmentsDropzone/AttachmentsTooManyFilesModal';

export type AttachmentsProviderProps = {
  handleLinkAttachment?: (unlinkedAttachment: AttachmentDraft) => Promise<void>;
  onDeleteAttachment?: (attachment: Attachment) => void;
  children?: React.ReactNode;
  attachments?: Attachment[];
  /**
   * The total number of attachments across all pages.
   * Used for calculating max attachment limits in pagination scenarios.
   * If not provided, falls back to the local attachments array length.
   */
  totalAttachments?: number;
  /** Controlled attachment ID — when set, opens this attachment */
  controlledAttachmentId?: string;
  /**
   * Callback fired when the viewer opens or closes — by `controlledAttachmentId`,
   * by a row click, or by `useOpenAttachment()`. Fires at most once per direction
   * per viewer session: switching attachments while the viewer stays open does not
   * re-fire `true`, and every `true` is paired with exactly one `false` on close.
   * A consumer syncing a URL param never sees a repeated or unmatched signal.
   */
  onViewerOpenChange?: (open: boolean) => void;
  /**
   * Callback fired when a non-previewable file is handled outside the viewer
   * (PDF opens in a new tab, or the file downloads). Fires from two paths:
   * - `useOpenAttachment()` — always, since that hook handles every file type
   * - `controlledAttachmentId` — only when `shouldAutoHandleNonViewable` is true
   */
  onFileHandled?: () => void;
  /**
   * When true, mimics click behavior: opens PDFs in new tab and downloads non-previewable files.
   * When false (default), only opens previewable files in viewer; others do nothing.
   */
  shouldAutoHandleNonViewable?: boolean;
  uploadIndicationIds?: string[];
  loading?: boolean;
  showLoadMore?: boolean;
  onLoadMore?: () => void;
  errorLoadingAttachments?: boolean;
  onRetryLoadingAttachments?: () => void;

  onBeforeUpload?: () => void;
  onAfterUpload?: () => void;
  onUploadFailed?: (error?: unknown) => void;

  onBeforeDelete?: () => void;
  onAfterDelete?: () => void;
  onDeleteFailed?: (error?: unknown) => void;
  onDeleteDraft?: (draftId: string) => void;

  onBeforeDownload?: (attachment: Attachment) => void;
  onAfterDownload?: (attachment: Attachment) => void;
  onDownloadFailed?: (error?: unknown) => void;

  onAttachmentClick?: (attachment: Attachment) => void;

  hideAttachmentUploadIndication?: (attachmentId: string) => void;

  maxFileSize?: number;
  maxNumberOfAttachments?: number;
  onCopyFromProjectFiles?: (files: EntityItem[]) => Promise<void>;

  enableUploadOnPaste?: boolean;

  allowDelete?: boolean;
  readOnly?: boolean;

  // will be passed to the container div
  containerClassName?: string;
  containerStyle?: CSSProperties;
};

const AttachmentsProvider: ComponentType<AttachmentsProviderProps> = ({
  children,
  onDeleteAttachment,
  handleLinkAttachment,
  enableUploadOnPaste = true,
  attachments: attachmentsFromProp,
  loading,
  showLoadMore,
  onLoadMore,
  errorLoadingAttachments,
  onRetryLoadingAttachments,
  onBeforeUpload,
  onAfterUpload,
  onUploadFailed,
  onBeforeDelete,
  onAfterDelete,
  onDeleteFailed,
  onDeleteDraft,
  maxFileSize,
  maxNumberOfAttachments,
  totalAttachments,
  onCopyFromProjectFiles,
  allowDelete = true,
  readOnly = false,
  containerStyle,
  containerClassName,
  onBeforeDownload,
  onAfterDownload,
  onDownloadFailed,
  onAttachmentClick,
  controlledAttachmentId,
  onViewerOpenChange,
  onFileHandled,
  shouldAutoHandleNonViewable,
  ...props
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [projectFilesModalOpen, setProjectFilesModalOpen] = useState(false);
  const [
    displayTooManyAttachmentsErrorModal,
    setDisplayTooManyAttachmentsErrorModal,
  ] = useState(false);
  const onTooManyAttachmentsError = useCallback(() => {
    setDisplayTooManyAttachmentsErrorModal(true);
  }, []);
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const {
    attachments,
    drafts,
    uploadIndicationIds,
    hideAttachmentUploadIndication,
    addAttachments,
    deleteAttachment,
    deleteDraft,
    discardUnlinkedDrafts,
    downloadAttachment,
    openPDFAttachmentInNewTab,
    retryDraftUpload,
    contextualTrack,
    isUploading,
    handleRejectedAttachments,
  } = useAttachmentsManager({
    maxFileSize,
    attachments: attachmentsFromProp,
    onDeleteAttachment,
    handleLinkAttachment,
    // Upload hooks
    onBeforeUpload,
    onAfterUpload,
    onUploadFailed,
    // Delete hooks
    onBeforeDelete,
    onAfterDelete,
    onDeleteFailed,
    onDeleteDraft,
    // Download hooks
    onBeforeDownload,
    onAfterDownload,
    onDownloadFailed,
    onTooManyAttachmentsError,
  });

  useEffect(() => {
    contextualTrack('prodex.attachments.view', {
      component_name: 'attachments',
    });
  }, [contextualTrack]);

  const { notifyCapacityLimitIfNeeded } = useCapacityAlert({
    maxNumberOfAttachments,
  });

  const uploadOnPaste = useCallback(
    (files: File[]) => {
      const totalAttachmentsCount =
        (totalAttachments ?? attachments.length) + drafts.length;

      const isExceedsMaxNumberOfAttachments =
        !!maxNumberOfAttachments &&
        files.length + totalAttachmentsCount > maxNumberOfAttachments;

      if (isExceedsMaxNumberOfAttachments) {
        onTooManyAttachmentsError?.();
        return;
      }

      notifyCapacityLimitIfNeeded({ totalAttachmentsCount });
      addAttachments({
        files,
        method: UPLOAD_METHODS.PASTE,
      });
    },
    [
      addAttachments,
      totalAttachments,
      attachments.length,
      drafts.length,
      maxNumberOfAttachments,
      notifyCapacityLimitIfNeeded,
      onTooManyAttachmentsError,
    ],
  );

  useCallbackOnPasteFiles({
    callback: enableUploadOnPaste ? uploadOnPaste : noop,
    ref,
  });

  useBrowserConfirmationBeforeTabClose({
    message: t('LEAVE_ALERT'),
    active: isUploading,
  });

  const onSaveCopies = useCallback(
    async (files: EntityItem[]) => {
      setIsSaving(true);

      const totalAttachmentsCount =
        (totalAttachments ?? attachments.length) + drafts.length;

      const isExceedsMaxNumberOfAttachments =
        !!maxNumberOfAttachments &&
        files.length + totalAttachmentsCount > maxNumberOfAttachments;

      if (isExceedsMaxNumberOfAttachments) {
        setDisplayTooManyAttachmentsErrorModal(true);
        setIsSaving(false);
        return;
      }

      notifyCapacityLimitIfNeeded({ totalAttachmentsCount });

      try {
        await onCopyFromProjectFiles?.(files);
      } catch (error) {
        throw new Error('Failed to copy attachments', { cause: error });
      } finally {
        setIsSaving(false);
        setProjectFilesModalOpen(false);
      }
    },
    [
      maxNumberOfAttachments,
      onCopyFromProjectFiles,
      totalAttachments,
      attachments.length,
      drafts.length,
      notifyCapacityLimitIfNeeded,
    ],
  );

  return (
    <AttachmentsContext.Provider
      value={{
        drafts,
        attachments,
        totalAttachments,
        loading,
        uploadIndicationIds,
        hideAttachmentUploadIndication,
        maxNumberOfAttachments,
        allowDelete,
        readOnly,
        addAttachments,
        deleteAttachment,
        deleteDraft,
        discardUnlinkedDrafts,
        showLoadMore,
        onLoadMore,
        downloadAttachment,
        openPDFAttachmentInNewTab,
        retryDraftUpload,
        errorLoadingAttachments,
        onRetryLoadingAttachments,
        onAttachmentClick,
        contextualTrack,
        isUploading,
        enableUploadOnPaste,
        handleRejectedAttachments,
        openProjectFilesModal: onCopyFromProjectFiles
          ? () => setProjectFilesModalOpen(true)
          : undefined,
      }}
    >
      <AttachmentsViewerProvider
        controlledAttachmentId={controlledAttachmentId}
        onOpenChange={onViewerOpenChange}
        onFileHandled={onFileHandled}
        shouldAutoHandleNonViewable={shouldAutoHandleNonViewable}
      >
        <Box
          style={containerStyle}
          className={containerClassName}
          {...props}
          ref={ref}
        >
          {children}
        </Box>
        {onCopyFromProjectFiles && (
          <CopyAttachmentsFromProjectFilesModal
            open={projectFilesModalOpen}
            onClose={() => setProjectFilesModalOpen(false)}
            onSave={onSaveCopies}
            isSaving={isSaving}
          />
        )}
        <AttachmentsTooManyFilesModal
          maxNumberOfAttachments={maxNumberOfAttachments}
          open={displayTooManyAttachmentsErrorModal}
          onClose={() => setDisplayTooManyAttachmentsErrorModal(false)}
        />
      </AttachmentsViewerProvider>
    </AttachmentsContext.Provider>
  );
};

const AttachmentsProviderWrapper = ({
  children,
  ...props
}: AttachmentsProviderProps) => (
  <LocalViewsProvider>
    <AttachmentsProvider {...props}>{children}</AttachmentsProvider>
  </LocalViewsProvider>
);

export default AttachmentsProviderWrapper;
